import { BrowserRouter, useLocation } from 'react-router-dom';
import { Provider } from 'react-redux';
import { HelmetProvider } from 'react-helmet-async';
import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import store from './store/store';
import AppRoutes from './AppRoutes';
import { getNewToken } from './services/authApi';
import { bookmarksApi } from './services/bookmarksApi';
import { materialsApi } from './services/materialsApi';
import { useHeartbeat } from './hooks/useHeartbeat';

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const AppContent = () => {
  const theme = useSelector((state) => state.ui.theme);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const dispatch = useDispatch();
  useHeartbeat();

  useEffect(() => {
    document.body.classList.toggle('light-theme', theme === 'light');
    document.body.style.background = '';
  }, [theme]);

  const prevIsAuthenticated = useRef(isAuthenticated);

  useEffect(() => {
    const wasAuthenticated = prevIsAuthenticated.current;
    prevIsAuthenticated.current = isAuthenticated;

    if (!wasAuthenticated && isAuthenticated) {
      // false → true: восстановление сессии
      dispatch(bookmarksApi.util.invalidateTags(['Bookmarks']));
      dispatch(materialsApi.util.invalidateTags(['Materials']));
    } else if (wasAuthenticated && !isAuthenticated) {
      // true → false: logout — сбрасываем кэш с user_meta
      dispatch(materialsApi.util.invalidateTags(['Materials']));
      dispatch(bookmarksApi.util.resetApiState());
    }
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem('access_token');
      const savedUser = localStorage.getItem('user');

      // Токен есть в localStorage — Redux уже инициализирован из него (authSlice initialState).
      // Не проверяем валидность здесь: RTK Query сам обнаружит 401 и сделает refresh.
      if (savedToken && savedUser) return;

      // Нет токена в localStorage — пробуем восстановить сессию только если
      // пользователь был залогинен раньше (auth_session убирается при явном logout)
      const hadSession = localStorage.getItem('auth_session');
      if (!hadSession) return;

      try {
        await getNewToken();
      } catch {
        // сессия истекла — нормально
      }
    };

    initAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <AppRoutes />
    </BrowserRouter>
  );
};

const App = () => {
  return (
    <HelmetProvider>
      <Provider store={store}>
        <AppContent />
      </Provider>
    </HelmetProvider>
  );
};

export default App;
